import React, { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Pagination from './Pagination';
import NewsListSkeleton from './NewsListSkeleton';
import NewsRowList from './NewsRowList';
import PageSeo from './seo/PageSeo';
import { claimAnalyticsAction, trackEvent } from '../amplitude';
import { normalizeSearchText } from '../utils/searchText';
import { useSearchNewsQuery } from '../hooks/useNewsListQuery';
import '../styles/SearchResults.css';

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const rawSearchTerm = searchParams.get('q')?.trim() || '';
  const currentPage = Number(searchParams.get('page')) || 1;

  const newsPerPage = 25;
  const neededLimit = currentPage * newsPerPage;

  const trackedSearchRef = useRef(null);
  const {
    news: searchNews,
    initialLoading,
    isRefreshing,
    totalCount,
  } = useSearchNewsQuery(rawSearchTerm, neededLimit);
  const totalPages = Math.max(1, Math.ceil(totalCount / newsPerPage));

  useEffect(() => {
    if (!rawSearchTerm || initialLoading) return;

    const normalizedQuery = normalizeSearchText(rawSearchTerm).slice(0, 100);
    const submissionKey = location.state?.searchSubmissionId || location.key;
    const key = `${normalizedQuery}:${submissionKey}`;

    if (trackedSearchRef.current === key) return;

    trackedSearchRef.current = key;
    if (!claimAnalyticsAction(`search:${key}`)) return;

    trackEvent('search', {
      query_length: normalizedQuery.length,
      normalized_query: normalizedQuery,
      results_count: totalCount,
      has_results: totalCount > 0,
      search_source: 'search_page',
    });
  }, [
    initialLoading,
    location.key,
    location.state?.searchSubmissionId,
    rawSearchTerm,
    totalCount,
  ]);

  const handlePageChange = (page) => {
    setSearchParams({
      q: rawSearchTerm,
      page: String(page),
    });

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const startIndex = (currentPage - 1) * newsPerPage;
  const pageNews = searchNews.slice(startIndex, startIndex + newsPerPage);

  return (
    <div className="panel panel--spaced" aria-busy={isRefreshing}>
      <PageSeo
        title="Пошук новин | BigSPORT"
        description="Пошук спортивних новин, команд, турнірів та спортсменів на BigSPORT."
        canonicalPath="/search"
        robots="noindex, follow"
      />
      <div className="search-news-layout news-content-wrapper">
        <h1>
          {rawSearchTerm
            ? `${rawSearchTerm} - останні новини`
            : 'Результати пошуку'}
        </h1>

        {rawSearchTerm && initialLoading && searchNews.length === 0 ? (
          <NewsListSkeleton count={8} variant="search" />
        ) : searchNews.length > 0 ? (
          <>
            <NewsRowList
              news={pageNews}
              startIndex={startIndex}
              firstImageEager
              headingLevel={2}
              onNewsClick={(news, index) => trackEvent(
                'search_result_clicked',
                {
                  query: normalizeSearchText(rawSearchTerm).slice(0, 100),
                  news_id: news.id,
                  position: index + 1,
                  category: news.category || null,
                }
              )}
            />

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                disabled={isRefreshing}
                sequential
                pageType="search"
              />
            )}
          </>
        ) : (
          <p>
            {rawSearchTerm
              ? `За тегом "${rawSearchTerm}" нічого не знайдено.`
              : 'Нічого не знайдено.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
